// src/lib/auth.js
// Centralized client-side auth state. The whole app reads/writes JWT and the
// cached user object through these helpers so we never sprinkle
// localStorage.getItem('token') across the codebase.
//
// Storage choice: localStorage. We are aware this is XSS-readable; the tradeoff
// is that the existing app already keeps the user object in localStorage and
// XSS is mitigated by the React render path (no eval, no dangerouslySetInnerHTML
// of arbitrary user content). Migration to httpOnly cookies is reserved for
// Sprint 3.5 once the auth flow is stable end-to-end.

const TOKEN_KEY = 'bullfit:token';
const USER_KEY = 'bullfit:user';
// Legacy key used by the pre-Sprint-3 app. We keep reading from it on boot
// so users that already have a session don't get logged out by the upgrade.
const LEGACY_USER_KEY = 'user';

const safeParse = (raw) => {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch (_) { return null; }
};

export const getToken = () => {
  try { return window.localStorage.getItem(TOKEN_KEY) || null; } catch (_) { return null; }
};

export const setToken = (token) => {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch (_) { /* no-op: private mode / quota */ }
};

export const getStoredUser = () => {
  try {
    const v2 = safeParse(window.localStorage.getItem(USER_KEY));
    if (v2) return v2;
    return safeParse(window.localStorage.getItem(LEGACY_USER_KEY));
  } catch (_) { return null; }
};

export const setStoredUser = (user) => {
  try {
    if (user) {
      window.localStorage.setItem(USER_KEY, JSON.stringify(user));
      // Mirror to the legacy key so the rest of the app (which still reads
      // localStorage.getItem('user')) keeps working without being touched
      // in this sprint.
      window.localStorage.setItem(LEGACY_USER_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(USER_KEY);
      window.localStorage.removeItem(LEGACY_USER_KEY);
    }
  } catch (_) { /* no-op */ }
};

export const clearAuth = () => {
  setToken(null);
  setStoredUser(null);
};

/**
 * True if the stored user is an admin. Uses role from the JWT-era payload
 * when available, falls back to the legacy hardcoded check so that a session
 * that predates this deploy is still recognized as admin.
 *
 * The legacy fallback is removed in Sprint 3.D once every active user has
 * been issued a token with the role claim.
 */
export const isAdmin = (user) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  // Legacy fallback - remove in Sprint 3.D.
  return user.Phone === '0000' && user.IdentificationNumber === '1234';
};
